import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { getBankAccountDetailLines } from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { DataMipPaymentRes, TransactionState } from '@dealstack/shared/lib/generated';

export const PAYMENT_STATUSES = [
	TransactionState.PENDING,
	TransactionState.INCOMPLETE,
	TransactionState.COMPLETED,
];

export const PAYMENT_STATUS_SUMMARY_ORDER = [
	TransactionState.COMPLETED,
	TransactionState.PENDING,
	TransactionState.INCOMPLETE,
];

export const getPaymentStatusColor = (status: TransactionState) => {
	switch (status) {
		case TransactionState.COMPLETED:
			return defaultTheme.v3.colors.teal[8];
		case TransactionState.PENDING:
			return defaultTheme.v3.colors.orange[6];
		case TransactionState.INCOMPLETE:
			return defaultTheme.v3.colors.red[6];
		default:
			return defaultTheme.v3.colors.dark[6];
	}
};

export const formatPaymentStatusText = (status: TransactionState) =>
	`${status.charAt(0)}${status.slice(1).toLowerCase()}`;

export const getInvestorName = (payment: DataMipPaymentRes) => payment.investorName || LONG_DASH;

export const getBankAccountDetails = (payment: DataMipPaymentRes) =>
	getBankAccountDetailLines({
		bankName: payment.bankName,
		currency: payment.currency,
		identifiers: payment.bankIdentifiers,
		accountNumber: payment.bankAccountNumber,
		iban: payment.bankIban,
	});

export const formatPaymentAmount = (
	value: number,
	currency?: string,
	currencyDisplay: keyof Intl.NumberFormatOptionsCurrencyDisplayRegistry = 'code'
) =>
	formatNumber({
		value,
		currency,
		lockPrecision: true,
		minPrecision: 2,
		maxPrecision: 2,
		returnNumericalZero: true,
		currencyDisplay,
	});

export const getPaymentStatusCount = (
	payments: DataMipPaymentRes[],
	status: TransactionState,
	countsByStatus?: Record<string, number>
) =>
	countsByStatus?.[status] ??
	payments.filter((payment) => payment.paymentStatus === status).length;

export const getLatestActivityUniqueCount = (payments: DataMipPaymentRes[]) =>
	new Set(
		payments
			.filter((payment) => !!payment.updatedAt)
			.map((payment) => formatStringDateToUI(payment.updatedAt))
	).size;

export const getAccountDetailsCount = (payments: DataMipPaymentRes[]) =>
	payments.filter((payment) => getBankAccountDetails(payment).hasBankAccount).length;
